import React, { useCallback, useState, useMemo, useRef, useEffect } from "react"
import { Input, Tooltip, Divider, Switch, Alert, Dropdown } from "antd"
import { notify } from "shared/ui/Notifications/notify"
import type { MenuProps } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faMagnifyingGlass,
  faRotateRight,
  faDownload,
  faUpload,
  faPenToSquare,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { Loader } from "shared/ui/Loader/Loader.component"
import { useExportMapStore } from "stores/exportMapStore"
import { useProjectStore } from "stores/projectStore"
import {
  exportMapToCSV,
  parseExportMapCSV,
  groupVariables,
} from "./utils/exportMapParser"
import {
  mergeWithOriginalMap,
  resolveDuplicateCodes,
} from "./utils/exportMapMerger"
import ExportMapProfileSelector from "./ExportMapProfileSelector.component"
import ExportMapTree from "./ExportMapTree.component"
import ExportMapBulkPrefixModal from "./ExportMapBulkPrefixModal.component"
import ExportMapCategorySetModal from "./ExportMapCategorySetModal.component"
import ExportMapMaxMultiSetModal from "./ExportMapMaxMultiSetModal.component"
import ExportMapSourceFillModal from "./ExportMapSourceFillModal.component"
import { downloadCsvAsFile } from "utils/files/downloadUtils"

interface ExportMapEditorProps {
  onCancel: () => void
  onApply: () => void
  onSaveAndExport: () => void
  onRegenerate: () => Promise<void>
  isSaving: boolean
  maxHeight?: string
}

/**
 * Main editor component for export map configuration.
 * Contains toolbar, profile selector, and variable tree.
 */
const ExportMapEditor: React.FC<ExportMapEditorProps> = ({
  onCancel,
  onApply,
  onSaveAndExport,
  onRegenerate,
  isSaving,
  maxHeight = "600px",
}) => {
  const {
    searchTerm,
    setSearchTerm,
    groups,
    setGroups,
    rootVariables,
    setRootVariables,
    allVariables,
    setVariables,
    hasChanges,
    useOriginalCodes,
    setUseOriginalCodes,
    originalCsv,
    originalMapCsv,
    toggleMaxMultiAll,
    isAllMaxMulti,
  } = useExportMapStore()
  const { project } = useProjectStore()

  const [isTogglingCodes, setIsTogglingCodes] = useState(false)
  const [showBulkPrefixModal, setShowBulkPrefixModal] = useState(false)
  const [showCategorySetModal, setShowCategorySetModal] = useState(false)
  const [showMaxMultiSetModal, setShowMaxMultiSetModal] = useState(false)
  const [showSourceFillModal, setShowSourceFillModal] = useState(false)
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm)
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null)

  // Cleanup debounce on unmount
  useEffect(() => {
    const debounceTimer = searchDebounceRef.current
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer)
      }
    }
  }, [])

  // Compute duplicate codes from allVariables (reactive to changes)
  const duplicateCodes = useMemo(() => {
    const codeToLabels = new Map<string, string[]>()
    for (const v of allVariables) {
      const code = v.variableCode.toUpperCase()
      if (!code) continue
      const existing = codeToLabels.get(code) || []
      existing.push(v.fullLabel)
      codeToLabels.set(code, existing)
    }
    // Filter to only duplicates
    const duplicates = new Map<string, string[]>()
    for (const [code, labels] of codeToLabels) {
      if (labels.length > 1) {
        duplicates.set(code, labels)
      }
    }
    return duplicates
  }, [allVariables])

  const hasDuplicates = duplicateCodes.size > 0

  const allMaxMulti = isAllMaxMulti()

  // Bulk edit dropdown menu items
  const bulkEditMenuItems: MenuProps["items"] = [
    {
      key: "prefix-suffix",
      label: "Add Prefix/Suffix to Variables",
      onClick: () => setShowBulkPrefixModal(true),
    },
    {
      key: "category-codes",
      label: "Set Category Codes by Text",
      onClick: () => setShowCategorySetModal(true),
    },
    {
      key: "max-multi-codes",
      label: "Set Max-Multi Codes by Text",
      onClick: () => setShowMaxMultiSetModal(true),
    },
    {
      key: "fill-from-source",
      label: "Fill from source document…",
      onClick: () => setShowSourceFillModal(true),
    },
    { type: "divider" },
    {
      key: "max-multi-all",
      label: (
        <div className="flex items-center justify-between gap-4">
          <span>{allMaxMulti ? "Disable" : "Enable"} Max-Multi All</span>
          <Switch
            size="small"
            checked={allMaxMulti}
            onChange={(checked, e) => {
              e.stopPropagation()
              toggleMaxMultiAll(checked)
            }}
            style={{ backgroundColor: allMaxMulti ? "#36c3ed" : undefined }}
          />
        </div>
      ),
      onClick: () => toggleMaxMultiAll(!allMaxMulti),
    },
  ]

  /**
   * Handle search input change with debounce for better performance
   */
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      setLocalSearchTerm(value)

      // Debounce the store update to reduce filtering overhead
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current)
      }
      searchDebounceRef.current = setTimeout(() => {
        setSearchTerm(value)
      }, 200)
    },
    [setSearchTerm]
  )

  /**
   * Handle toggling between original and auto-generated codes
   */
  const handleUseOriginalCodesChange = useCallback(
    (checked: boolean) => {
      setIsTogglingCodes(true)
      setUseOriginalCodes(checked)

      // Use setTimeout to allow UI to update before processing
      setTimeout(() => {
        // Re-merge variables with new setting
        const generatedVariables = parseExportMapCSV(originalCsv)
        const mergeResult = mergeWithOriginalMap(
          generatedVariables,
          originalMapCsv,
          checked
        )
        setVariables(mergeResult.variables)

        // Re-group for tree display
        const { groups: updatedGroups, rootVariables: updatedRootVars } =
          groupVariables(mergeResult.variables)
        setGroups(updatedGroups)
        setRootVariables(updatedRootVars)
        setIsTogglingCodes(false)
      }, 50)
    },
    [
      originalCsv,
      originalMapCsv,
      setUseOriginalCodes,
      setVariables,
      setGroups,
      setRootVariables,
    ]
  )

  /**
   * Handle reset - regenerates export map with current varType
   */
  const handleRegenerate = useCallback(async () => {
    await onRegenerate()
    notify.toast.success("Export map regenerated")
  }, [onRegenerate])

  /**
   * Handle export CSV download
   */
  const handleExportCsv = useCallback(() => {
    const csv = exportMapToCSV(allVariables)
    downloadCsvAsFile(csv, "export_map.csv")
    notify.toast.success("Export map downloaded")
  }, [allVariables])

  /**
   * Handle import CSV upload
   */
  const handleImportCsv = useCallback(() => {
    const input = document.createElement("input")
    input.type = "file"
    input.accept = ".csv"
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (!file) return

      try {
        const text = await file.text()
        const importedVariables = parseExportMapCSV(text)

        if (importedVariables.length === 0) {
          notify.toast.error("No valid variables found in CSV")
          return
        }

        // Merge imported codes with current variables (match by label)
        let matchCount = 0
        const matchedLabels = new Set<string>()
        const updatedVariables = allVariables.map((v) => {
          const imported = importedVariables.find(
            (iv) => iv.fullLabel === v.fullLabel
          )
          if (imported) {
            matchCount++
            matchedLabels.add(imported.fullLabel)
            return {
              ...v,
              variableCode: imported.variableCode,
              categories: v.categories?.map((c) => {
                const importedCat = imported.categories?.find(
                  (ic) => ic.label === c.label
                )
                return importedCat ? { ...c, code: importedCat.code } : c
              }),
            }
          }
          return v
        })

        // Find unmatched rows in imported CSV
        const unmatchedLabels = importedVariables
          .filter((iv) => !matchedLabels.has(iv.fullLabel))
          .map((iv) => iv.fullLabel)

        setVariables(updatedVariables)
        const { groups: updatedGroups, rootVariables: updatedRootVars } =
          groupVariables(updatedVariables)
        setGroups(updatedGroups)
        setRootVariables(updatedRootVars)

        if (matchCount === 0) {
          notify.toast.warning(
            "No variables matched. Labels in CSV must match existing variable labels."
          )
        } else if (unmatchedLabels.length > 0) {
          notify.toast.success(`Imported codes for ${matchCount} variables`)
          notify.toast.warning(
            `${
              unmatchedLabels.length
            } row(s) in CSV did not match: ${unmatchedLabels
              .slice(0, 3)
              .join(", ")}${unmatchedLabels.length > 3 ? "..." : ""}`
          )
        } else {
          notify.toast.success(`Imported codes for ${matchCount} variables`)
        }
      } catch (error) {
        console.error("Import failed:", error)
        notify.toast.error("Failed to import CSV")
      }
    }
    input.click()
  }, [allVariables, setVariables, setGroups, setRootVariables])

  /**
   * Handle deduplicating INT/CAT variable code pairs
   */
  const handleDeduplicateCodes = useCallback(() => {
    const resolved = resolveDuplicateCodes(allVariables)
    setVariables(resolved)
    const { groups: updatedGroups, rootVariables: updatedRootVars } =
      groupVariables(resolved)
    setGroups(updatedGroups)
    setRootVariables(updatedRootVars)
    notify.toast.success("Duplicate INT/CAT variable codes resolved")
  }, [allVariables, setVariables, setGroups, setRootVariables])

  return (
    <div className="flex flex-col" style={{ height: maxHeight }}>
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2 flex-shrink-0">
        <div className="flex items-center gap-2">
          <ExportMapProfileSelector />
        </div>

        <div className="flex items-center gap-2">
          <Tooltip
            title={
              originalMapCsv
                ? "Use codes from original map.csv when available"
                : "No original map.csv available"
            }
          >
            <div className="flex items-center gap-1">
              <Switch
                checked={useOriginalCodes}
                onChange={handleUseOriginalCodesChange}
                size="small"
                disabled={!originalMapCsv || isTogglingCodes}
                style={{
                  backgroundColor:
                    useOriginalCodes && originalMapCsv ? "#36c3ed" : undefined,
                }}
              />
              <span
                className={`text-sm ${
                  originalMapCsv ? "text-gray-600" : "text-gray-400"
                }`}
              >
                Use Original Codes
              </span>
            </div>
          </Tooltip>
          <Dropdown
            menu={{ items: bulkEditMenuItems }}
            trigger={["click"]}
            disabled={isTogglingCodes}
          >
            <DataQButton type="secondary" isDisabled={isTogglingCodes}>
              <FontAwesomeIcon icon={faPenToSquare} className="mr-1" />
              Bulk Edit
              <FontAwesomeIcon icon={faChevronDown} className="ml-1 text-xs" />
            </DataQButton>
          </Dropdown>
          <Tooltip title="Regenerate export map">
            <DataQButton
              type="secondary"
              onClick={handleRegenerate}
              isDisabled={isTogglingCodes}
            >
              <FontAwesomeIcon icon={faRotateRight} className="mr-1" />
              Reset
            </DataQButton>
          </Tooltip>
          <Tooltip title="Import CSV">
            <DataQButton
              type="secondary"
              onClick={handleImportCsv}
              isDisabled={isTogglingCodes}
            >
              <FontAwesomeIcon icon={faUpload} className="mr-1" />
              Import
            </DataQButton>
          </Tooltip>
          <Tooltip title="Export CSV">
            <DataQButton
              type="secondary"
              onClick={handleExportCsv}
              isDisabled={isTogglingCodes}
            >
              <FontAwesomeIcon icon={faDownload} className="mr-1" />
              Export
            </DataQButton>
          </Tooltip>
        </div>
      </div>

      {/* Search */}
      <div className="mb-2 flex-shrink-0">
        <Input
          placeholder="Search variables..."
          prefix={<FontAwesomeIcon icon={faMagnifyingGlass} className="text-gray-400" />}
          value={localSearchTerm}
          onChange={handleSearchChange}
          allowClear
          onClear={() => {
            setLocalSearchTerm("")
            setSearchTerm("")
          }}
        />
      </div>

      {/* Duplicate Codes Warning */}
      {hasDuplicates && (
        <Alert
          type="warning"
          showIcon
          className="mb-2 flex-shrink-0"
          message={
            <div className="flex items-center justify-between">
              <span>
                <strong>Duplicate variable codes:</strong>{" "}
                {Array.from(duplicateCodes.entries())
                  .slice(0, 3)
                  .map(
                    ([code, labels]) =>
                      `"${code}" (${labels.length} variables)`
                  )
                  .join(", ")}
                {duplicateCodes.size > 3 &&
                  ` and ${duplicateCodes.size - 3} more`}
              </span>
              <DataQButton
                type="secondary"
                onClick={handleDeduplicateCodes}
                size="small"
              >
                Deduplicate
              </DataQButton>
            </div>
          }
        />
      )}

      {/* Scrollable Content Area */}
      <div className="flex-grow overflow-auto min-h-0">
        {isTogglingCodes ? (
          <div className="flex flex-col items-center justify-center h-full border rounded-lg">
            <Loader fontSize={32} color="#36c3ed" />
            <p className="mt-2 text-gray-500 text-sm">Updating codes...</p>
          </div>
        ) : (
          <div className="border rounded-lg">
            <ExportMapTree
              groups={groups}
              rootVariables={rootVariables}
              searchTerm={searchTerm}
            />
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 flex-shrink-0">
        <Tooltip title="Code from original map.csv file">
          <span className="cursor-help">
            <span
              className="inline-block px-1 py-0.5 rounded text-white text-[9px] font-medium mr-1"
              style={{ backgroundColor: "#52c41a" }}
            >
              ORIG
            </span>
            From original map
          </span>
        </Tooltip>
        <Tooltip title="Code was auto-generated (no original map or variable not in map)">
          <span className="cursor-help">
            <span
              className="inline-block px-1 py-0.5 rounded text-white text-[9px] font-medium mr-1"
              style={{ backgroundColor: "#faad14" }}
            >
              AUTO
            </span>
            Auto-generated
          </span>
        </Tooltip>
        {hasChanges && (
          <Tooltip title="You have unsaved changes to the export map">
            <span className="text-yellow-600 font-medium ml-auto cursor-help">
              Unsaved changes
            </span>
          </Tooltip>
        )}
      </div>

      <Divider className="my-2 flex-shrink-0" />

      {/* Footer Actions */}
      <div className="flex justify-end gap-2 flex-shrink-0">
        <DataQButton
          type="secondary"
          onClick={onCancel}
          isDisabled={isTogglingCodes}
        >
          Cancel
        </DataQButton>
        <DataQButton
          type="primary"
          onClick={onApply}
          isDisabled={isTogglingCodes}
        >
          Export
        </DataQButton>
        <DataQButton
          type="primary"
          onClick={onSaveAndExport}
          isLoading={isSaving}
          isDisabled={isTogglingCodes}
        >
          Save Profile & Export
        </DataQButton>
      </div>

      {/* Bulk Edit Modals */}
      <ExportMapBulkPrefixModal
        open={showBulkPrefixModal}
        onClose={() => setShowBulkPrefixModal(false)}
      />
      <ExportMapCategorySetModal
        open={showCategorySetModal}
        onClose={() => setShowCategorySetModal(false)}
      />
      <ExportMapMaxMultiSetModal
        open={showMaxMultiSetModal}
        onClose={() => setShowMaxMultiSetModal(false)}
      />
      <ExportMapSourceFillModal
        open={showSourceFillModal}
        onClose={() => setShowSourceFillModal(false)}
        projectId={project.id}
      />
    </div>
  )
}

export default ExportMapEditor
